// "Add from Hey Captain library": lists the library entries this vessel does not have yet, the user ticks some and adds them.
// Generic: `kind` is a library kind (src/lib/libraryAdd.js - RA_LIBRARY today; contingency plans pass theirs), `loadLibrary`
// returns the library file (loaded only when the picker opens). Everything is written as the logged-in user (the database's
// rules apply) through the same engine as scripts/import-ra.mjs, so an entry already on the vessel is never added twice and a
// checklist of the same title is reused. A kind may write its entries itself (kind.add, e.g. contingency plans: one database
// function); Preview shows an entry's content (kind.preview), "Add all" adds every entry not on the vessel in one go, and
// LibraryTag marks a vessel record that came from the library - the same for every kind (RAs, contingency plans).
import { useEffect, useState } from 'react'
import { supabase } from '../lib/supabase'
import { shipTodayAuto } from '../lib/shipTime'
import { planAdd, toWrite, runAdd } from '../lib/libraryAdd'

// the vessel's rows + the library's checklists already on it (with their number of steps), through the login's session
export async function loadExisting(kind, lib, vesselId, today) {
  const { data: rows, error } = await supabase.from(kind.table).select(kind.existingSelect).eq('client_id', vesselId).limit(5000)
  if (error) throw new Error(error.message)
  const titles = [...new Set(kind.items(lib).map(it => (kind.map(it, today).checklist || {}).title).filter(Boolean))]
  let checklists = []
  if (titles.length) {
    const { data: cl, error: ce } = await supabase.from('checklists').select('id,title').eq('client_id', vesselId).in('title', titles)
    if (ce) throw new Error(ce.message)
    checklists = cl || []
    if (checklists.length) {
      const { data: its } = await supabase.from('checklist_items').select('checklist_id').in('checklist_id', checklists.map(c => c.id)).limit(20000)
      const n = {}; for (const i of its || []) n[i.checklist_id] = (n[i.checklist_id] || 0) + 1
      checklists = checklists.map(c => ({ ...c, items: n[c.id] || 0 }))
    }
  }
  return { rows: rows || [], checklists }
}
// the write adapter for runAdd: inserts as the login
export const supabaseApi = {
  async insert(table, body, o = {}) {
    let q = supabase.from(table).insert(body)
    if (o.returning) q = q.select()
    const { data, error } = await q
    if (error) throw new Error((error.code ? error.code + ' ' : '') + error.message)
    return data
  },
  // a database function, as the login (its own rights check applies)
  async rpc(fn, args) {
    const { data, error } = await supabase.rpc(fn, args)
    if (error) throw new Error((error.code ? error.code + ' ' : '') + error.message)
    return data
  },
}
// the "Library" tag on a vessel record that came from the Hey Captain library (an RA, an adopted contingency plan ...)
export function LibraryTag({ style }) {
  return <span className="pill pill-x" title="From the Hey Captain library - this copy is the vessel's own" style={{ flexShrink: 0, ...style }}>Library</span>
}

export default function LibraryPicker({ kind, loadLibrary, vesselId, onClose, onAdded, note, ctx }) {
  const [state, setState] = useState({ loading: true })
  const [picked, setPicked] = useState([])
  const [busy, setBusy] = useState(false)
  const [result, setResult] = useState(null)
  const [added, setAdded] = useState(0)
  const [preview, setPreview] = useState(null)
  const verb = kind.verb || 'Add'
  // the page reloads its list only when the picker closes (a reload while it is open would unmount it and lose the result)
  const close = () => { if (busy) return; if (added && onAdded) onAdded(added); onClose() }
  const today = shipTodayAuto()   // the ship's day (clients.timezone), not the device's
  async function refresh() {
    try {
      const mod = await loadLibrary()
      const lib = mod && mod.default ? mod.default : mod
      const plan = planAdd(kind, lib, await loadExisting(kind, lib, vesselId, today), today, ctx || {})
      setState({ lib, plan: toWrite(plan), have: plan.filter(p => p.action === 'skip' && !p.fillItems).length })
    } catch (e) { setState({ error: e.message }) }
  }
  useEffect(() => { refresh() }, [vesselId])
  const all = state.plan || []
  const toggle = code => setPicked(p => (p.includes(code) ? p.filter(x => x !== code) : [...p, code]))
  // names for the result line: the code, or the title when the kind has no codes
  const nameOf = code => { const p = all.find(x => x.code === code); return p ? (p.title && !kind.card(p.item).code ? p.title : code) : code }
  const named = r => ({ ...r, made: r.made.map(nameOf), filled: r.filled.map(nameOf), skipped: r.skipped.map(x => ({ ...x, code: nameOf(x.code) })), failed: r.failed.map(x => ({ ...x, code: nameOf(x.code) })), notDone: r.notDone.map(nameOf) })
  async function add(codes = picked) {
    setBusy(true); setResult(null)
    try {
      const r = named(await runAdd(kind, toWrite(all, codes), vesselId, supabaseApi))
      setResult(r); setPicked([])
      setAdded(x => x + r.made.length + r.filled.length)
      await refresh()
    } catch (e) { setResult({ made: [], filled: [], skipped: [], failed: [{ code: '-', error: e.message }], notDone: [] }) }
    finally { setBusy(false) }
  }
  return (
    <div style={st.overlay} onClick={close}>
      <div style={st.modal} onClick={e => e.stopPropagation()} role="dialog" aria-label={'Add ' + kind.label + ' from the Hey Captain library'}>
        <div style={st.title}>{verb} from Hey Captain library</div>
        {note && <div style={st.note}>{note}</div>}
        {state.loading && <div className="loading-row"><div className="spin" />Loading the library...</div>}
        {state.error && <div className="err-box">{/permit|checklist_id|column|library_id|function/i.test(state.error) ? 'The library needs ' + (kind.setupSql || 'sql/ra-library.sql') + ' to be run first. ' : ''}{state.error}</div>}
        {state.plan && (all.length === 0
          ? <div style={st.note}>Every library {kind.label.replace(/s$/, '')} is already on this vessel{state.have ? ' (' + state.have + ')' : ''}.</div>
          : <>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', margin: '4px 0 8px' }}>
              <span style={{ fontSize: 11, color: 'var(--muted)' }}>{all.length} not on this vessel yet{state.have ? ' · ' + state.have + (verb === 'Adopt' ? ' already adopted' : ' already added') : ''}</span>
              <span style={{ display: 'flex', gap: 6 }}>
                <button type="button" className="btn" style={st.sbtn} onClick={() => setPicked(picked.length === all.length ? [] : all.map(p => p.code))}>{picked.length === all.length ? 'Select none' : 'Select all'}</button>
                <button type="button" className="btn" style={st.sbtn} disabled={busy} onClick={() => { if (window.confirm(verb + ' all ' + all.length + ' ' + kind.label + ' from the library on this vessel?')) add(all.map(p => p.code)) }}>{verb} all ({all.length})</button>
              </span>
            </div>
            {all.map(p => {
              const c = kind.card(p.item), open = preview === p.code
              return (
                <div key={p.code} style={{ ...st.row, borderColor: picked.includes(p.code) ? 'var(--blue, #2E5BFF)' : 'var(--border)', flexDirection: 'column', gap: 6 }}>
                  <label style={{ display: 'flex', gap: 10, alignItems: 'flex-start', cursor: 'pointer' }}>
                    <input type="checkbox" checked={picked.includes(p.code)} onChange={() => toggle(p.code)} aria-label={(c.code ? c.code + ' ' : '') + c.title} />
                    <span style={{ flex: 1 }}>
                      {c.code && <b>{c.code} </b>}{c.title}{p.unfinished ? ' (' + p.unfinished + ')' : p.fillItems ? ' (finish adding its checklist)' : ''}
                      {c.lines.filter(Boolean).map((l, i) => <span key={i} style={st.line}>{l}</span>)}
                    </span>
                    {kind.preview && <button type="button" className="btn" style={st.sbtn} aria-expanded={open} onClick={e => { e.preventDefault(); setPreview(open ? null : p.code) }}>{open ? 'Hide' : 'Preview'}</button>}
                  </label>
                  {open && <ol style={st.preview}>{kind.preview(p.item).map((l, i) => <li key={i}>{l}</li>)}</ol>}
                </div>
              )
            })}
          </>)}
        {result && <div style={result.failed.length ? st.bad : st.good}>
          {result.made.length ? (verb === 'Adopt' ? 'Adopted ' : 'Added ') + result.made.length + ': ' + result.made.join(', ') + (kind.addedNote ? ' (' + kind.addedNote + ')' : '') + '. ' : ''}
          {result.filled.length ? 'Finished: ' + result.filled.join(', ') + '. ' : ''}
          {result.skipped.length ? 'Already on this vessel: ' + result.skipped.map(x => x.code).join(', ') + '. ' : ''}
          {result.failed.length ? 'Stopped at ' + result.failed[0].code + ': ' + result.failed[0].error + (result.notDone.length ? ' - not added yet: ' + result.notDone.join(', ') + '. Try again: what was added stays.' : '') : ''}
        </div>}
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 14 }}>
          <button type="button" className="btn" onClick={close} disabled={busy}>Close</button>
          <button type="button" className="btn btn-primary" onClick={() => add()} disabled={busy || !picked.length}>{busy ? (verb === 'Adopt' ? 'Adopting...' : 'Adding...') : verb + ' ' + (picked.length || '') + ' selected'}</button>
        </div>
      </div>
    </div>
  )
}

const st = {
  overlay: { position: 'fixed', top: 0, left: 0, width: '100vw', height: '100vh', background: 'rgba(0,0,0,.6)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '24px 16px', boxSizing: 'border-box', zIndex: 1000 },
  modal: { background: 'var(--ink2)', border: '0.5px solid var(--border)', borderRadius: 12, padding: '22px 24px', width: '100%', maxWidth: 640, maxHeight: '90vh', overflowY: 'auto', boxSizing: 'border-box' },
  title: { fontSize: 15, fontWeight: 600, color: 'var(--text)', marginBottom: 8 },
  note: { fontSize: 12, color: 'var(--muted)', marginBottom: 8 },
  sbtn: { fontSize: 11, padding: '3px 9px' },
  row: { display: 'flex', gap: 10, alignItems: 'flex-start', padding: '9px 10px', border: '0.5px solid var(--border)', borderRadius: 8, marginBottom: 6, cursor: 'pointer', fontSize: 13, color: 'var(--text)' },
  line: { display: 'block', fontSize: 11.5, color: 'var(--muted)', marginTop: 2 },
  preview: { margin: '2px 0 2px 26px', paddingLeft: 18, fontSize: 12, lineHeight: 1.5, color: 'var(--text)' },
  good: { fontSize: 12, color: 'var(--green, #2e7d32)', marginTop: 10 },
  bad: { fontSize: 12, color: 'var(--red, #c62828)', marginTop: 10 },
}
